import { Form, Button, Row, Col, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { useCreateFAQMutation } from "../../../redux/api/FAQApi/FAQApi";
import { FaPlus, FaSave, FaArrowLeft } from "react-icons/fa";
import "../Services/Services.css";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import { useFormik } from "formik";
import * as Yup from "yup";

const AddFAQ = () => {
  const navigate = useNavigate();
  const [createFAQ, { isLoading }] = useCreateFAQMutation();

  const validationSchema = Yup.object({
    question: Yup.string().trim().required("Question is required"),
    answer: Yup.string().trim().required("Answer is required"),
    display_order: Yup.number(),
    status: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      question: "",
      answer: "",
      display_order: 0,
      status: true,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await createFAQ(values).unwrap();
        message.success("FAQ created successfully!");
        setTimeout(() => navigate("/admin/faq"), 2000);
      } catch (error) {
        message.error(
          error.data?.error || error.message || "Failed to create FAQ",
        );
      }
    },
  });

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="container py-4 fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaPlus className="text-primary me-2" />
            Add New FAQ
          </h2>
          <p className="text-muted mb-0">
            Create a new entry for your knowledge base
          </p>
        </div>
        <Button
          variant="outline-secondary"
          className="d-flex align-items-center gap-2 shadow-sm rounded-pill px-4"
          onClick={() => navigate("/admin/faq")}
        >
          <FaArrowLeft /> Back to List
        </Button>
      </div>

      <Form onSubmit={formik.handleSubmit}>
        <Card className="border-0 overflow-hidden">
          <Card.Body className="p-4 p-md-5">
            <h5 className="mb-4 text-secondary border-start border-4 border-primary ps-3">
              FAQ Information
            </h5>
            <Row className="g-4">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Question <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="question"
                    {...formik.getFieldProps("question")}
                    isInvalid={
                      formik.touched.question && !!formik.errors.question
                    }
                    placeholder="Type the question here..."
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.question}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Answer <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="answer"
                    {...formik.getFieldProps("answer")}
                    isInvalid={formik.touched.answer && !!formik.errors.answer}
                    placeholder="Provide a detailed answer..."
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.answer}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    name="display_order"
                    {...formik.getFieldProps("display_order")}
                  />
                  <Form.Text className="text-muted small">
                    Lower numbers appear first in the list.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6} className="d-flex align-items-center mt-md-auto mt-4">
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="faq-status"
                    label="Active / Published"
                    name="status"
                    checked={formik.values.status}
                    onChange={formik.handleChange}
                    className="fw-bold text-primary"
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-3 mt-5">
              <Button
                variant="light"
                className="rounded-pill px-4"
                onClick={() => navigate("/admin/faq")}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                className="rounded-pill px-5 shadow-sm d-flex align-items-center gap-2"
              >
                <FaSave /> Save FAQ
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Form>
    </div>
  );
};

export default AddFAQ;
